import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, MapPin, Image as ImageIcon, Sparkles, X, Video as VideoIcon, Play } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { waLink } from "../constants/data";
import { Sparkle, Heart, Flower, Cupcake } from "../components/doodles/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const msg = (name) =>
  `Hi Naathi Charami! 👋 I'd love to enquire about your "${name}" service (Hyderabad). Please share themes, lead time & pricing. Thank you!`;

export default function Combos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/catalogues`)
      .then((r) => {
        const found = (r.data || []).find((c) => c.slug === "combo-showcase");
        setData(found || null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Lock scroll + close on Escape
  useEffect(() => {
    const open = lightbox !== null || activeVideo !== null;
    const onKey = (e) => { if (e.key === "Escape") { setLightbox(null); setActiveVideo(null); } };
    if (open) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox, activeVideo]);

  if (loading) {
    return <div className="pt-32 pb-20 text-center font-hand text-[#5C5555]">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="pt-32 pb-20 text-center" data-testid="combos-not-found">
        <p className="font-doodle text-3xl text-[#7A1F2B]">Combo showcase not available yet</p>
        <Link to="/" className="btn-primary mt-4 inline-flex">Back home</Link>
      </div>
    );
  }

  const images = (data.images || []).filter(Boolean).slice(0, 30);
  const videos = (data.videos || []).filter(Boolean).slice(0, 6);
  const wa = waLink(msg(data.name || "Combo Party Orders"));

  return (
    <div className="pt-28 pb-20 min-h-[80vh] relative overflow-hidden" data-testid="combos-page">
      <Helmet>
        <title>Combo Party Orders in Hyderabad — Naathi Charami</title>
        <meta name="description" content={(data.description || "End-to-end party combos in Hyderabad — cake, table grazing, photographer, decor and return gifts.").slice(0, 160)}/>
      </Helmet>

      <div className="absolute inset-0 -z-10" style={{
        background: "radial-gradient(circle at 20% 20%, #FFE0B5 0%, transparent 45%), radial-gradient(circle at 80% 80%, #FFD4DC 0%, transparent 45%), linear-gradient(135deg, #FFF7E6 0%, #FFE7F1 100%)",
      }}/>

      <Sparkle color="#FFD93D" className="absolute top-32 left-10 w-6 animate-twinkle"/>
      <Sparkle color="#FF6B9D" className="absolute top-40 right-20 w-5 animate-twinkle" style={{ animationDelay: "0.5s" }}/>
      <Heart color="#FF6B9D" className="absolute bottom-32 right-12 w-7 animate-bounce-doodle"/>
      <Flower color="#FFA94D" className="absolute -top-3 -left-3 w-24 rotate-12 opacity-80"/>
      <Flower color="#FF6B9D" className="absolute -bottom-4 -right-4 w-24 -rotate-12 opacity-80"/>

      <div className="container-x relative">
        <Link to="/" data-testid="combos-back-link" className="inline-flex items-center gap-1.5 font-hand text-sm text-[#7A1F2B] hover:underline mb-3">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border-2 border-[#7A1F2B] font-hand text-[11px] uppercase tracking-widest text-[#7A1F2B] -rotate-2 rounded-full"
                style={{ boxShadow: "3px 3px 0 #7A1F2B" }}>
            <MapPin className="w-3 h-3"/> In Hyderabad
          </span>
          <h1 className="font-doodle text-3xl sm:text-4xl lg:text-5xl text-[#7A1F2B] mt-3 leading-tight" data-testid="combos-name">
            Combo Party <span className="text-[#FF6B9D]">Orders</span>
          </h1>
          {data.description && (
            <p className="mt-3 font-hand text-base text-[#2D2424] leading-relaxed whitespace-pre-line" data-testid="combos-description">
              {data.description}
            </p>
          )}
          {data.product_info && (
            <p className="mt-2 font-hand text-sm text-[#7A1F2B] italic" data-testid="combos-product-info">
              {data.product_info}
            </p>
          )}
        </div>

        {/* WhatsApp CTA */}
        <div className="mt-5 text-center">
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="combos-whatsapp-btn"
            className="btn-whatsapp inline-flex"
          >
            <MessageCircle className="w-4 h-4"/> Enquire on WhatsApp
          </a>
        </div>

        {/* Important notes */}
        {data.important_notes && (
          <div className="mt-6 max-w-2xl mx-auto px-3 py-2 bg-[#FFF7DC] border-2 border-dashed border-[#7A1F2B]/40 rounded-xl">
            <p className="text-[10px] font-doodle uppercase tracking-widest text-[#7A1F2B] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3"/> Important
            </p>
            <p className="font-hand text-xs text-[#2D2424] mt-1 whitespace-pre-line">{data.important_notes}</p>
          </div>
        )}

        {/* Image gallery */}
        <section className="mt-10" data-testid="combos-gallery">
          <h2 className="font-doodle text-2xl text-[#7A1F2B] mb-3 flex items-center gap-2">
            <ImageIcon className="w-5 h-5"/> Our previous work
          </h2>
          {images.length === 0 ? (
            <div className="aspect-[4/3] max-w-2xl mx-auto bg-[#FFE7F1] border-2 border-dashed border-[#7A1F2B]/40 rounded-3xl flex flex-col items-center justify-center gap-2 text-[#9B9494]">
              <Cupcake className="w-14 opacity-80"/>
              <p className="font-hand text-sm">Images coming soon</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="combos-image-grid">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(i)}
                  data-testid={`combos-image-${i}`}
                  className="aspect-square doodle-card overflow-hidden bg-[#FFE7F1] hover:-translate-y-0.5 transition-transform"
                  style={{ boxShadow: "4px 4px 0 #2D2424" }}
                >
                  <img src={src} alt={`Combo party order ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" loading="lazy"/>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Videos */}
        {videos.length > 0 && (
          <section className="mt-10" data-testid="combos-videos">
            <h2 className="font-doodle text-2xl text-[#7A1F2B] mb-3 flex items-center gap-2">
              <VideoIcon className="w-5 h-5"/> Videos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {videos.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveVideo(src)}
                  data-testid={`combos-video-${i}`}
                  className="relative aspect-video doodle-card overflow-hidden bg-black/80 hover:-translate-y-0.5 transition-transform"
                  style={{ boxShadow: "4px 4px 0 #2D2424" }}
                >
                  <video src={src} className="w-full h-full object-cover opacity-90" muted preload="metadata"/>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                    <div className="w-12 h-12 rounded-full bg-white/95 border-2 border-[#2D2424] flex items-center justify-center" style={{ boxShadow: "3px 3px 0 #2D2424" }}>
                      <Play className="w-5 h-5 text-[#7A1F2B] ml-0.5" fill="#7A1F2B"/>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className="font-hand text-sm text-[#5C5555] mb-3 italic">Loved what you saw?</p>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="combos-whatsapp-bottom"
            className="btn-whatsapp inline-flex"
          >
            <MessageCircle className="w-4 h-4"/> Book your combo on WhatsApp
          </a>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div
          className="fixed inset-0 z-[100] bg-[#2D2424]/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          data-testid="combos-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
            aria-label="close"
          >
            <X className="w-5 h-5"/>
          </button>
          <img src={images[lightbox]} alt="" className="max-w-full max-h-[88vh] object-contain rounded-2xl"/>
        </div>
      )}

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-[#2D2424]/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}
          data-testid="combos-video-modal"
        >
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
            aria-label="close"
          >
            <X className="w-5 h-5"/>
          </button>
          <video src={activeVideo} controls autoPlay playsInline className="max-w-full max-h-[88vh] rounded-2xl bg-black"/>
        </div>
      )}
    </div>
  );
}
